import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { useCopiar } from '@common/hooks';
import { formatarDataHora as dataHoraIso, formatarDataHoraCurta } from '@common/i18n/formatosData';
import { formatarEndereco } from '@common/utils/endereco';
import { formatarEnderecoExterior } from '@common/utils/enderecoExterior';
import {
  Alert,
  Badge,
  Button,
  Drawer,
  EmptyState,
  Separator,
  Spinner,
  useToast,
} from '@ntconsult/aluma-react';
import { Check, Link } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDetalheParticipante, useRetentarGravacao } from '../hooks';
import type { AcaoLote, DetalheParticipante, EnderecoOficial } from '../types/Painel';
import { ConviteBadge } from './ConviteBadge';
import { GravacaoBadge } from './GravacaoBadge';
import { linkDaCampanha } from './LinkCampanhaSecao';
import { PreviaEmailModal } from './PreviaEmailModal';
import { TextoParaCopiar } from './TextoParaCopiar';

const dataHora = (iso: string | null) => (iso ? dataHoraIso(iso) : '—');

const dash = (v: string | null | undefined) => (v?.trim() ? v : '—');
type Endereco = {
  rua: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  cep: string | null;
};

/** Formato do resto do app (R38): `Rua, 77 - compl. – Bairro – Cidade/UF – CEP 00000-000`. */
const formatar = (e: Endereco) =>
  formatarEndereco({
    endereco: dash(e.rua),
    numero: dash(e.numero),
    complemento: e.complemento?.trim() || null,
    bairro: dash(e.bairro),
    cidade: dash(e.cidade),
    uf: dash(e.uf),
    cep: e.cep,
  });

/** Spec 11: `paisId` não nulo e diferente de 1 (o apresentado só traz o nome do país). */
const noExterior = (o: EnderecoOficial) =>
  o.paisId != null ? o.paisId !== 1 : !!o.paisNome && o.paisNome !== 'Brasil';

/** Endereço oficial: brasileiro como antes; fora do Brasil, texto livre com a região e o país. */
function formatarOficial(o: EnderecoOficial) {
  if (!noExterior(o)) return formatar(doOficial(o));
  // Canvas X05: `endereço – bairro – cidade[, região][ código postal] – país`, sem segmentos vazios.
  const cidade = [o.cidadeNome, o.regiao ?? o.estadoNome].filter((x) => x?.trim()).join(', ');
  return [
    [o.endereco, o.numero, o.complemento].filter((x) => x?.trim()).join(', '),
    o.distrito,
    [cidade, o.cep].filter((x) => x?.trim()).join(' '),
    o.paisNome,
  ]
    .filter((x) => x?.trim())
    .join(' – ');
}

const doOficial = (o: EnderecoOficial): Endereco => ({
  rua: o.endereco,
  numero: o.numero,
  complemento: o.complemento,
  bairro: o.distrito,
  cidade: o.cidadeNome,
  uf: o.estadoSigla,
  cep: o.cep,
});

function Linhas({ itens }: { itens: [string, React.ReactNode][] }) {
  return (
    <dl className="grid grid-cols-1 text-sm sm:grid-cols-[12rem_1fr] sm:gap-y-2">
      {itens.map(([rotulo, valor]) => (
        <div key={rotulo} className="contents">
          <dt className="text-foreground-muted">{rotulo}</dt>
          <dd className="mb-2 break-words sm:mb-0">{valor}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Pergunta em `foreground-muted` e a resposta embaixo (R33). */
function Pares({ itens }: { itens: [string, React.ReactNode][] }) {
  return (
    <dl className="flex flex-col gap-3 text-sm">
      {itens.map(([pergunta, resposta]) => (
        <div key={pergunta} className="flex flex-col gap-0.5">
          <dt className="text-foreground-muted">{pergunta}</dt>
          <dd className="break-words">{resposta}</dd>
        </div>
      ))}
    </dl>
  );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-foreground">{titulo}</h2>
      {children}
    </section>
  );
}

type Props = {
  campanhaId: string;
  campanhaAtiva: boolean;
  /** Campanha recebendo respostas (agendada ou aberta): o link da campanha vale (spec 31 R15). */
  podeCopiarLink: boolean;
  participanteId: string | null;
  onClose: () => void;
  /** Excluir/devolver pelo Drawer: a confirmação é da lista. */
  onAcao: (acao: AcaoLote, d: DetalheParticipante) => void;
};

export function ParticipanteDetalhe({
  campanhaId,
  campanhaAtiva,
  podeCopiarLink,
  participanteId,
  onClose,
  onAcao,
}: Props) {
  const { t } = useTranslation([
    'participantes',
    'formulario',
    'common',
    'erros',
    'convites',
    'escritas',
  ]);
  const { toast } = useToast();
  const { data: d, isLoading, isError, error } = useDetalheParticipante(campanhaId, participanteId);
  const retentar = useRetentarGravacao(campanhaId);
  const [verEmail, setVerEmail] = useState(false);
  const { copiar, copiado, manual } = useCopiar();
  const r = d?.resposta;
  const simNao = (v: boolean) => t(v ? 'common:yes' : 'common:no');
  const podeRetentar =
    campanhaAtiva &&
    !!d?.escritasAxiis.some((e) => e.status === 'falha' || e.status === 'pendente');

  async function aoRetentar() {
    if (!participanteId) return;
    try {
      const res = await retentar.mutateAsync(participanteId);
      if (res.tentadas === 0) toast.info({ title: t('participantes:detalhe.retentadoNada') });
      else
        toast.success({
          title: t('participantes:detalhe.retentado', { count: res.sucesso, falha: res.falha }),
        });
    } catch (e) {
      toast.danger({ title: t(`erros:${mensagemDeErro(e)}`) });
    }
  }

  // Pergunta literal do formulário e a resposta dada (R33).
  const pares: [string, React.ReactNode][] = [];
  if (r) {
    pares.push([t('formulario:brinde.pergunta'), simNao(r.desejaBrinde)]);
    if (r.tamanho) {
      const apresentado = r.apresentado.camiseta;
      const decisao = r.tamanho.decisao;
      pares.push([
        // O passo "tamanho" da spec 33: o atual em destaque, "Manter {{tamanho}}" ou "Trocar".
        apresentado
          ? `${t('formulario:camiseta.titulo')}: ${apresentado}`
          : t('formulario:camiseta.escolha'),
        decisao === 'manter'
          ? t('formulario:camiseta.manterTamanho', { tamanho: apresentado ?? '' })
          : decisao === 'alterar'
            ? `${t('formulario:camiseta.trocar')} · ${dash(r.tamanho.tamanhoFinal)}`
            : dash(r.tamanho.tamanhoFinal),
      ]);
    }
    if (r.entrega)
      pares.push([
        t('formulario:entrega.titulo'),
        t(`formulario:entrega.${r.entrega.origem ?? 'outro'}`),
      ]);
  }

  return (
    <Drawer
      open={!!participanteId}
      onClose={onClose}
      size="xl"
      ariaLabel={t('participantes:detalhe.titulo')}
    >
      <Drawer.Header
        title={d?.nomes.exibicao ?? t('participantes:detalhe.titulo')}
        subtitle={
          d && (
            <span className="flex flex-wrap items-center gap-2">
              {t('participantes:detalhe.matricula', { matricula: d.matricula ?? '—' })}
              <Badge tone={d.situacao === 'elegivel' ? 'success' : 'neutral'}>
                {t(`participantes:detalhe.${d.situacao}`)}
              </Badge>
            </span>
          )
        }
        onClose={onClose}
        dismissLabel={t('common:close')}
      />
      <Drawer.Body className="flex flex-col gap-6">
        {isLoading && <Spinner label={t('common:loading')} />}
        {isError && <Alert variant="danger" title={t(`erros:${mensagemDeErro(error)}`)} />}
        {d && (
          <>
            <Secao titulo={t('participantes:detalhe.secoes.identificacao')}>
              <Linhas
                itens={[
                  [t('participantes:colunas.email'), dash(d.email)],
                  [t('participantes:detalhe.nome'), d.nomes.nome],
                  [t('participantes:detalhe.nomeSocial'), dash(d.nomes.nomeSocial)],
                  [t('participantes:detalhe.nomePrefer'), dash(d.nomes.nomePrefer)],
                  ...(d.exclusao
                    ? [[t('participantes:colunas.por'), dash(d.exclusao.por)] as [string, string]]
                    : []),
                  [t('participantes:detalhe.incluidoEm'), dataHora(d.incluidoEm)],
                  [
                    t('participantes:colunas.origem'),
                    t(`participantes:origem.${d.incluidoOrigem}`),
                  ],
                  [
                    t('participantes:detalhe.primeiroAcesso'),
                    dash(d.primeiroAcessoEm && dataHora(d.primeiroAcessoEm)),
                  ],
                  [
                    t('participantes:colunas.ultimoAcesso'),
                    d.ultimoAcessoEm
                      ? dataHora(d.ultimoAcessoEm)
                      : t('participantes:acessos.nunca'),
                  ],
                  [t('participantes:colunas.acessos'), String(d.acessos)],
                  [t('participantes:detalhe.fotografiaEm'), dataHora(d.fotografadoEm)],
                ]}
              />
            </Secao>
            <Separator />

            {!r ? (
              <Secao titulo={t('participantes:detalhe.secoes.respostas')}>
                <EmptyState title={t('participantes:detalhe.semResposta')} />
              </Secao>
            ) : (
              <>
                <Secao titulo={t('participantes:detalhe.secoes.apresentado')}>
                  <Linhas
                    itens={[
                      [t('participantes:detalhe.camiseta'), dash(r.apresentado.camiseta)],
                      [
                        t('participantes:detalhe.enderecoApresentado'),
                        formatarOficial(r.apresentado.enderecoOficial),
                      ],
                    ]}
                  />
                </Secao>
                <Separator />
                <Secao titulo={t('participantes:detalhe.secoes.respostas')}>
                  <Pares itens={pares} />
                  <Linhas
                    itens={[
                      [t('participantes:detalhe.versao'), String(r.versao)],
                      [t('participantes:detalhe.primeiraResposta'), dataHora(r.respondidaEm)],
                      [t('participantes:detalhe.ultimaResposta'), dataHora(r.atualizadaEm)],
                    ]}
                  />
                </Secao>
                {r.entrega && (
                  <>
                    <Separator />
                    <Secao
                      titulo={
                        r.entrega.origem === 'exterior'
                          ? t('formulario:resumo.entregaExterior')
                          : t('participantes:detalhe.secoes.entrega')
                      }
                    >
                      <p className="text-sm">
                        {r.entrega.origem === 'exterior'
                          ? formatarEnderecoExterior(r.entrega)
                          : formatar({
                              rua: r.entrega.logradouro,
                              numero: r.entrega.numero,
                              complemento: r.entrega.complemento,
                              bairro: r.entrega.bairro,
                              cidade: r.entrega.cidade,
                              uf: r.entrega.uf,
                              cep: r.entrega.cep,
                            })}
                      </p>
                    </Secao>
                  </>
                )}
                <Separator />
                <Secao titulo={t('participantes:detalhe.secoes.oficial')}>
                  <p className="text-sm">
                    {r.enderecoOficial.desatualizado
                      ? formatarOficial(r.enderecoOficial)
                      : t('participantes:detalhe.semAlteracaoOficial')}
                  </p>
                </Secao>
                {r.confirmacoes && (
                  <>
                    <Separator />
                    <Secao titulo={t('participantes:detalhe.secoes.confirmacoes')}>
                      <p className="text-sm">
                        {t('participantes:detalhe.confirmacoesLinha', {
                          correto: simNao(r.confirmacoes.enderecoCorreto).toLowerCase(),
                          recebedor: simNao(r.confirmacoes.recebedorDisponivel).toLowerCase(),
                        })}
                      </p>
                    </Secao>
                  </>
                )}
              </>
            )}

            {d.escritasAxiis.length > 0 && (
              <>
                <Separator />
                <Secao titulo={t('participantes:detalhe.secoes.axiis')}>
                  <ul className="flex flex-col gap-3 text-sm">
                    {d.escritasAxiis.map((e) => (
                      <li key={e.item} className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {t(
                              e.item === 'camiseta'
                                ? 'participantes:detalhe.itemCamiseta'
                                : 'participantes:detalhe.itemEndereco',
                            )}
                          </span>
                          <GravacaoBadge status={e.status} />
                          {e.incompleto && <Badge tone="warning">{t('escritas:incompleto')}</Badge>}
                        </div>
                        <span className="text-foreground-muted">
                          {t(
                            e.tentativas === 1
                              ? 'participantes:detalhe.tentativa'
                              : 'participantes:detalhe.tentativas',
                            {
                              n: e.tentativas,
                            },
                          )}
                          {' · '}
                          {e.ultimoErro ??
                            (e.ultimaTentativaEm
                              ? formatarDataHoraCurta(e.ultimaTentativaEm)
                              : '—')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Secao>
              </>
            )}

            <Separator />
            <Secao titulo={t('participantes:detalhe.secoes.convite')}>
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <ConviteBadge
                    status={d.convite.status}
                    resultado={d.convite.resultado}
                    respondeu={!!d.resposta}
                  />
                  <span className="text-foreground-muted">{dataHora(d.convite.atualizadoEm)}</span>
                </div>
                {d.convite.resultado && (
                  <span className="text-foreground-muted">
                    {t('convites:detalhe.resultado')}: {d.convite.resultado}
                  </span>
                )}
                {d.situacao === 'elegivel' && (
                  <button
                    type="button"
                    className="self-start text-primary underline"
                    onClick={() => setVerEmail(true)}
                  >
                    {t('convites:previa.verPessoa')}
                  </button>
                )}
                {d.historicoConvites.length === 0 ? (
                  <span className="text-foreground-muted">
                    {t('convites:detalhe.semTentativas')}
                  </span>
                ) : (
                  <ul className="flex flex-col gap-1">
                    {d.historicoConvites.map((h) => (
                      <li key={h.tentadoEm}>
                        {dataHora(h.tentadoEm)} · {t(`convites:status.${h.resultado}`)}
                        {h.detalhe ? ` – ${h.detalhe}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Secao>
          </>
        )}
      </Drawer.Body>
      {d && manual && (
        <div className="px-6 pb-2">
          <TextoParaCopiar texto={manual.texto} rotulo={t('convites:lembrar.copiarLink')} />
        </div>
      )}
      {d && (
        <Drawer.Footer>
          {podeRetentar && (
            <Button loading={retentar.isPending} onClick={aoRetentar}>
              {t('participantes:detalhe.retentar')}
            </Button>
          )}
          {/* O mesmo link da campanha: aqui o admin decide falar com a pessoa por outro canal (C12). */}
          {podeCopiarLink && d.situacao === 'elegivel' && (
            <Button
              variant="secondary"
              leftIcon={copiado ? <Check size={14} /> : <Link size={14} />}
              onClick={() => void copiar('link', linkDaCampanha(campanhaId))}
            >
              {t(copiado ? 'convites:lembrar.linkCopiado' : 'convites:lembrar.copiarLink')}
            </Button>
          )}
          <Button
            variant="secondary" // excluir se desfaz; o primário é o "Tentar de novo" (spec 32 R5, R9)
            onClick={() => onAcao(d.situacao === 'elegivel' ? 'excluir' : 'restaurar', d)}
          >
            {t(
              d.situacao === 'elegivel'
                ? 'participantes:lote.excluir'
                : 'participantes:lote.devolver',
            )}
          </Button>
        </Drawer.Footer>
      )}
      {participanteId && (
        <PreviaEmailModal
          campanhaId={campanhaId}
          participanteId={participanteId}
          open={verEmail}
          onClose={() => setVerEmail(false)}
        />
      )}
    </Drawer>
  );
}
